import type { ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import { Badge } from '@/shared/ui/badge'
import { t } from '@/shared/lib/i18n'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import { LIFECYCLE_BADGE, type LifecycleStatus } from '@/entities/lot/model/lifecycleStatus'
import { LotsExpireWarning } from '@/entities/lot/ui/LotsExpireWarning'

/** Shape of a linked or used lot row displayed read-only in the form. */
export type LotRow = {
  id: number
  name: string | null
  lotNumber: string | null
  expirationDate: string | null
  currentLotDetailId: number | null
  lifecycleStatus: LifecycleStatus | null
  isLotRetired: boolean
  isLotDiscarded: boolean
}

type LotReadOnlyRowProps = {
  lot: LotRow
  changeSlot?: ReactNode
  /** IANA site timezone; falls back to shared tenantTimeZone when omitted. */
  siteTimeZone?: string | null
}

/** Read-only lot row with name, lot number, expiration, lifecycle status, and 7-day warning. */
export const LotReadOnlyRow = ({ lot, changeSlot, siteTimeZone }: LotReadOnlyRowProps) => {
  const { tenantTimeZone } = usePage<{ tenantTimeZone?: string | null }>().props
  const timeZone = siteTimeZone ?? tenantTimeZone ?? null
  const expirationLabel = lot.expirationDate
    ? formatCalendarDate(lot.expirationDate)
    : t('instruments.taskLogs.noExpiration')

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          <span className="font-medium">{lot.name ?? '—'}</span>
          {lot.lotNumber && <span className="text-muted-foreground"> – {lot.lotNumber}</span>}
          <span className="text-muted-foreground"> – {expirationLabel}</span>
        </span>
        {lot.lifecycleStatus != null && (
          <Badge
            variant={LIFECYCLE_BADGE[lot.lifecycleStatus] ?? 'outline'}
            data-testid="lot-lifecycle-status"
          >
            {t(`instruments.lotsIndex.lifecycleStatus.${lot.lifecycleStatus}`)}
          </Badge>
        )}
        <LotsExpireWarning
          expirationDate={lot.expirationDate}
          lifecycleStatus={lot.lifecycleStatus}
          timeZone={timeZone}
        />
      </div>
      {changeSlot}
    </div>
  )
}
